import { useCallback, useEffect, useMemo, useState } from 'react';

const ThreadSidebarTransitionMs = 220;
const ThreadTransitionTypes = {
    EXISTING: 'existing',
    NEW: 'new',
    OPEN: 'open',
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const isThreadTransitionReady = ({ chatId, chatViewKey, isChatRouteSynced, transition }) => {
    switch (transition.type) {
        case ThreadTransitionTypes.OPEN:
            return isChatRouteSynced;
        case ThreadTransitionTypes.NEW:
            return chatViewKey !== transition.previousChatViewKey;
        case ThreadTransitionTypes.EXISTING:
            return chatId === transition.threadId;
        default:
            return true;
    }
};

const useHugoThreadTransitions = ({
    chat,
    chatViewKey,
    isChatRouteSynced,
    isThreadSidebarExpanded,
    setIsThreadSidebarExpanded,
    threadListAdapter,
    threadSwitcher,
}) => {
    const [pendingThreadTransition, setPendingThreadTransition] = useState(null);

    const startOpenTransition = useCallback(() => {
        setPendingThreadTransition({
            previousChatViewKey: chatViewKey,
            type: ThreadTransitionTypes.OPEN,
        });
    }, [chatViewKey]);

    const clearPendingThreadTransition = useCallback(() => {
        setPendingThreadTransition(null);
    }, []);

    const animatedThreadListAdapter = useMemo(() => {
        if (!threadListAdapter) return threadListAdapter;

        return {
            ...threadListAdapter,
            onSwitchToNewThread: async () => {
                setPendingThreadTransition({
                    previousChatViewKey: chatViewKey,
                    type: ThreadTransitionTypes.NEW,
                });
                threadListAdapter.onSwitchToNewThread?.();
                setIsThreadSidebarExpanded(false);
            },
            onSwitchToThread: async (threadId) => {
                setPendingThreadTransition({
                    threadId,
                    type: ThreadTransitionTypes.EXISTING,
                });

                try {
                    if (isThreadSidebarExpanded) {
                        setIsThreadSidebarExpanded(false);
                        await wait(ThreadSidebarTransitionMs);
                    }

                    return await threadListAdapter.onSwitchToThread?.(threadId);
                } catch (error) {
                    setPendingThreadTransition(null);
                    throw error;
                }
            },
        };
    }, [chatViewKey, isThreadSidebarExpanded, setIsThreadSidebarExpanded, threadListAdapter]);

    useEffect(() => {
        if (!pendingThreadTransition) return undefined;

        const transitionReady = isThreadTransitionReady({
            chatId: chat.id,
            chatViewKey,
            isChatRouteSynced,
            transition: pendingThreadTransition,
        });

        if (!transitionReady) return undefined;

        const frameId = requestAnimationFrame(() => {
            setPendingThreadTransition(null);
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [chat.id, chatViewKey, isChatRouteSynced, pendingThreadTransition]);

    const displayedThreadSwitcher = {
        ...threadSwitcher,
        onSelect: animatedThreadListAdapter?.onSwitchToThread || threadSwitcher.onSelect,
        onStartNew: animatedThreadListAdapter?.onSwitchToNewThread || threadSwitcher.onStartNew,
    };

    return {
        animatedThreadListAdapter,
        clearPendingThreadTransition,
        displayedThreadSwitcher,
        pendingThreadTransition,
        startOpenTransition,
    };
};

export default useHugoThreadTransitions;
